package com.example.mystybag.e5AiSearch.helpers

import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.js.Promise
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * Web implementation of [PlatformOnnxRunner] using onnxruntime-web.
 * Model and tokenizer assets must be copied to the web output (e.g. by webpack CopyWebpackPlugin).
 */
internal class WebOnnxRunner(
    private val session: dynamic,
    private val outputName: String,
    private val maxLen: Int,
    private val dim: Int
) : PlatformOnnxRunner {

    override suspend fun run(tokens: Tokens): Array<FloatArray> = suspendCancellableCoroutine { cont ->
        val ort = js("require('onnxruntime-web')")
        val shape = arrayOf(1, maxLen)

        fun longArrayToBigInt64(longArr: LongArray): dynamic {
            val numbers = (0 until longArr.size).map { longArr[it].toDouble() }.toTypedArray()
            return js("(function(n){ return new BigInt64Array(n.map(function(x){ return BigInt(Math.floor(Number(x))); })); })")(numbers)
        }

        val createTensor = js("(function(ort, type, data, dims){ return new ort.Tensor(type, data, dims); })")
        val idsTensor = createTensor(ort, "int64", longArrayToBigInt64(tokens.inputIds), shape)
        val maskTensor = createTensor(ort, "int64", longArrayToBigInt64(tokens.attentionMask), shape)
        val typeTensor = createTensor(ort, "int64", longArrayToBigInt64(tokens.tokenTypeIds), shape)

        val feeds = js("{}")
        feeds["input_ids"] = idsTensor
        feeds["attention_mask"] = maskTensor
        feeds["token_type_ids"] = typeTensor

        val runPromise: Promise<dynamic> = session.run(feeds)
        runPromise.then(
            { result: dynamic ->
                val output = result[outputName]
                if (output == null || output.data == null) {
                    cont.resumeWithException(IllegalStateException("ONNX run: missing output $outputName"))
                    return@then
                }
                val data = output.data
                val hidden = Array(maxLen) { FloatArray(dim) }
                val getAt = js("(function(arr, i) { return Number(arr[i]); })")
                var idx = 0
                for (i in 0 until maxLen) {
                    for (d in 0 until dim) {
                        hidden[i][d] = (getAt(data, idx) as Double).toFloat()
                        idx++
                    }
                }
                cont.resume(hidden)
            },
            { err: dynamic ->
                cont.resumeWithException(Throwable("ONNX run failed: ${err?.message ?: err}"))
            }
        )
    }

    companion object {
        suspend fun create(
            modelUrl: String,
            maxLen: Int,
            dim: Int
        ): WebOnnxRunner = suspendCancellableCoroutine { cont ->
            val ort = js("require('onnxruntime-web')")
            val opts = js("{}")
            opts.executionProviders = arrayOf("wasm")

            val createPromise: Promise<dynamic> = ort.InferenceSession.create(modelUrl, opts)
            createPromise.then(
                { session: dynamic ->
                    val outputNames = session.outputNames
                    val outputName = if (outputNames != null && outputNames.length > 0) {
                        outputNames[0]
                    } else "last_hidden_state"
                    E5Platform.log("ORT", "Web session created output=$outputName")
                    cont.resume(WebOnnxRunner(session, outputName, maxLen, dim))
                },
                { err: dynamic ->
                    cont.resumeWithException(Throwable("ONNX session create failed: ${err?.message ?: err}"))
                }
            )
        }
    }
}
